import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Typography } from '@mui/material';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  EstateInputDataFormat_CreateEstateInputDataFormatInput,
  EstateInputDataFormat_FieldsFragment,
  EstateInputDataFormat_UpdateEstateInputDataFormatInput,
  EstateInputDataFormatDataType,
  useEstateInputDataFormat_CreateEstateInputDataFormatMutation,
  useEstateInputDataFormat_GetAllEstateInputDataFormatsQuery,
  useEstateInputDataFormat_UpdateEstateInputDataFormatMutation,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import HomeLayout from 'layouts/home-layout';
import { GridStateKey, restoreGridState, saveGridState } from 'utils/grid-state';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import CreateEstateInputDataFormatModal from './components/create-estate-input-data-format-modal';
import EditEstateInputDataFormatModal from './components/edit-estate-input-data-format-modal';
import { linkedEstateMatterFieldOptions } from './components/estate-input-data-format-form';

const useStyles = makeStyles((theme) => ({
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer': {
      paddingRight: 0,
      paddingLeft: 0,
    },
    width: '100%',
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  button: {
    backgroundColor: '#5ED1F0',
    fontSize: '12px',
    color: '#1C2541',
    margin: theme.spacing(2, 0),
  },
  pillContainer: {
    padding: '4px 8px',
    borderRadius: '10px',
    margin: '10px 0',
  },
  label: {
    textTransform: 'capitalize',
  },
}));

const SuperAdminRequiredInfoFormat = () => {
  const classes = useStyles();
  const history = useHistory();
  const [isAddEstateInputDataFormatModalOpen, setIsAddEstateInputDataFormatModalOpen] =
    useState(false);
  const [editingEstateInputDataFormat, setEditingEstateInputDataFormat] =
    useState<EstateInputDataFormat_FieldsFragment | null>(null);

  const gridApiRef = useGridApiRef();
  const gridApiCurrentRef = gridApiRef.current;

  const {
    data: estateInputDataFormaData,
    loading: loadingEstateInputDataFormats,
    refetch: refetchEstateInputDataFormats,
  } = useEstateInputDataFormat_GetAllEstateInputDataFormatsQuery({
    onError: (error) => {
      showAlert({
        message: 'Error getting all Estate Input Data Formats: ' + error.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleOnClick = (id: string) => {
    history.push(`/home/${id}/manage-estate-input-data-format-list-option`);
  };

  const columns = useMemo(() => {
    const gridColumns: GridColDef<EstateInputDataFormat_FieldsFragment>[] = [
      {
        disableColumnMenu: true,
        field: 'name',
        headerName: 'Name',
        flex: 2,
        valueGetter: ({ row }) => row.name,
      },
      {
        disableColumnMenu: true,
        field: 'labelHint',
        headerName: 'Label Hint',
        flex: 1,
        valueGetter: ({ row }) => row.labelHint || 'N/A',
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 1,
        renderCell: ({ row }) => <div className={classes.label}>{row.type.replace('_', ' ')}</div>,
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        renderCell: ({ row }) => (
          <div className={classes.label}>{row.category?.replace('_', ' ')}</div>
        ),
      },
      {
        field: 'subcategory',
        headerName: 'Subcategory',
        flex: 1,
        renderCell: ({ row }) => <div className={classes.label}>{row.subcategory}</div>,
      },
      {
        field: 'updateRule',
        headerName: 'Update Rule',
        flex: 1,
        renderCell: ({ row }) => (
          <div className={classes.label}>{row.updateRule?.replace('_', ' ')}</div>
        ),
      },
      {
        field: 'dataType',
        headerName: 'Data Type',
        flex: 1,
        renderCell: ({ row }) => (
          <div className={classes.label}>{row.dataType?.replace('_', ' ')}</div>
        ),
      },
      {
        field: 'listOptions',
        headerName: 'List Options',
        flex: 1,
        disableColumnMenu: true,
        disableReorder: true,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.dataType === EstateInputDataFormatDataType.List) {
            return (
              <>
                <IconButton
                  aria-label="Open list options"
                  onClick={() => {
                    handleOnClick(row.id);
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </>
            );
          }
          return <></>;
        },
      },
      {
        field: 'documentTag',
        headerName: 'Document Tag',
        flex: 1,
        renderCell: ({ row }) => {
          if (!row.documentTag) {
            return 'N/A';
          }

          return (
            <div
              style={{
                backgroundColor: row.documentTag?.pillBackgroundColor,
                color: row.documentTag?.pillTextColor,
              }}
              className={classes.pillContainer}
            >
              {row.name}
            </div>
          );
        },
        valueGetter: ({ row }) => row?.documentTag?.name || 'N/A',
      },
      {
        disableColumnMenu: true,
        field: 'linkedField',
        headerName: 'Linked Field',
        flex: 1,
        valueGetter: ({ row }) => {
          return row.linkedField ? linkedEstateMatterFieldOptions[row.linkedField] : 'N/A';
        },
      },
      {
        disableColumnMenu: true,
        field: 'templateFieldKey',
        headerName: 'Template Field Key',
        flex: 1,
        valueGetter: ({ row }) => row.templateFieldKey || 'N/A',
      },
      {
        disableColumnMenu: true,
        field: 'omitEmptyPlaceholder',
        headerName: 'Omit Empty Placeholder',
        flex: 1,
        valueGetter: ({ row }) => (row.omitEmptyPlaceholder ? ' Yes' : 'No'),
      },
      {
        field: 'action',
        flex: 1,
        headerName: '',
        renderCell: ({ row }) => {
          return (
            <Button onClick={() => setEditingEstateInputDataFormat(row)}>
              <EditIcon />
            </Button>
          );
        },
      },
    ];

    return gridColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes.label, classes.pillContainer]);

  const [createEstateInputDataFormat, { loading: createEstateInputDataFormatExecuting }] =
    useEstateInputDataFormat_CreateEstateInputDataFormatMutation({
      onCompleted: () => {
        setIsAddEstateInputDataFormatModalOpen(false);
        refetchEstateInputDataFormats();
        showToast({
          message: 'Estate Input Data Format created successfully',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating Estate Input Data Format: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateEstateInputDataFormat, { loading: updateEstateInputDataFormatExecuting }] =
    useEstateInputDataFormat_UpdateEstateInputDataFormatMutation({
      onCompleted: () => {
        setEditingEstateInputDataFormat(null);
        refetchEstateInputDataFormats();
        showToast({
          message: 'Estate Input Data Format updated successfully',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating Estate Input Data Format: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleCreateEstateInputDataFormat = (
    values: EstateInputDataFormat_CreateEstateInputDataFormatInput,
  ) => {
    createEstateInputDataFormat({
      variables: {
        input: values,
      },
    });
  };

  const handleUpdateEstateInputDataFormat = (
    input: EstateInputDataFormat_UpdateEstateInputDataFormatInput,
  ) => {
    updateEstateInputDataFormat({
      variables: {
        input,
      },
    });
  };

  useEffect(() => {
    restoreGridState(gridApiCurrentRef, GridStateKey.RequiredInformationFormatGrid);
  }, [gridApiRef, gridApiCurrentRef]);

  const handleSaveGridState = () => {
    saveGridState(gridApiCurrentRef, GridStateKey.RequiredInformationFormatGrid);
  };

  const getRequiredInformationFormatToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  return (
    <HomeLayout showPortalName={false}>
      <div className={classes.container}>
        <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
          <Typography className={classes.title} variant="h5" style={{ flex: 3 }}>
            <span onClick={() => history.push(`/home`)}>
              <span className={classes.routeIconContainer}>
                <img src="/home-icon.svg" alt="home-icon" />
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
            </span>
            Required Information Format
          </Typography>
        </div>
        <div>
          <Button
            variant="contained"
            type="button"
            className={classes.button}
            onClick={() => setIsAddEstateInputDataFormatModalOpen(true)}
          >
            Add a new Estate Input Data Format
          </Button>
        </div>
        <DataGrid
          apiRef={gridApiRef}
          loading={loadingEstateInputDataFormats && !estateInputDataFormaData}
          className={classes.root}
          columns={columns}
          rows={estateInputDataFormaData?.data || []}
          disableColumnReorder
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          autoHeight={true}
          rowHeight={70}
          pagination
          slots={{
            toolbar: getRequiredInformationFormatToolbar,
          }}
          onColumnVisibilityModelChange={() => {
            handleSaveGridState();
          }}
          onColumnWidthChange={() => {
            handleSaveGridState();
          }}
          onFilterModelChange={() => {
            handleSaveGridState();
          }}
        />
        {isAddEstateInputDataFormatModalOpen && (
          <CreateEstateInputDataFormatModal
            title="Add Estate Input Data Format"
            createEstateInputDataFormatExecuting={createEstateInputDataFormatExecuting}
            onClose={() => setIsAddEstateInputDataFormatModalOpen(false)}
            onSubmit={handleCreateEstateInputDataFormat}
          />
        )}
        {editingEstateInputDataFormat && (
          <EditEstateInputDataFormatModal
            estateInputDataFormat={editingEstateInputDataFormat}
            updateEstateInputDataFormatExecuting={updateEstateInputDataFormatExecuting}
            onClose={() => setEditingEstateInputDataFormat(null)}
            onSubmit={handleUpdateEstateInputDataFormat}
          />
        )}
      </div>
    </HomeLayout>
  );
};

export default SuperAdminRequiredInfoFormat;
